import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback } from 'react';

import { Alert, Box, Button, Flex, Modal, Name, Table, Text } from '~/components/blocks';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { samePatientModalState } from '~/state/patient/atoms';

import { useFetchPatient } from './use-fetch-patient';

const NotPatientButton = styled(Button)(({ theme }) =>
  css({
    border: theme.borders.default,
  }),
);

export const SamePatientModal = () => {
  const theme = useTheme();
  const { isOpen, guestPatientId, patientId } = useAtomValue(samePatientModalState);
  const resetModalState = useResetAtom(samePatientModalState);
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);
  const { patient } = useFetchPatient();

  const handleSubmitByPatient = useCallback(() => {
    if (patientId) {
      setPatientDetailDialogState((_state) => ({
        ..._state,
        isOpen: true,
        type: 'message' as TabType,
        patientId: patientId,
      }));
      resetModalState();
    }
  }, [patientId, setPatientDetailDialogState, resetModalState]);

  const handleSubmitByOriginal = useCallback(() => {
    setPatientDetailDialogState((_state) => ({
      ..._state,
      isOpen: true,
      type: 'message' as TabType,
      patientId: guestPatientId,
    }));
    resetModalState();
  }, [guestPatientId, setPatientDetailDialogState, resetModalState]);

  const handleClose = useCallback(() => resetModalState(), [resetModalState]);

  if (!patient) return null;

  return (
    <Modal open={isOpen} size="s" onClose={handleClose}>
      <Modal.Header>
        <Text size="m" marginRight={theme.space.xxl}>
          メッセージ送信先の確認
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Alert status="info">
          <Text>
            該当患者は、melmoを使用しています。
            <br />
            アプリ宛にメッセージを作成しませんか？
          </Text>
          <Text fontWeight="bold">
            <u>アプリ宛に作成するメリット</u>
            <br />
            <li>メッセージ内容を最大300文字まで入力できる</li>
          </Text>
        </Alert>
        <Box
          backgroundColor={theme.colors.colorPallete.grey04}
          marginTop={theme.space.m}
          padding={theme.space.l}
        >
          <Table appearance="noline" whiteSpace="nowrap">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>患者名</Table.Th>
                <Table.Th>生年月日</Table.Th>
                <Table.Th>送信先</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Name
                    size="l"
                    familyName={patient.familyName}
                    givenName={patient.givenName}
                    phoneticFamilyName={patient.phoneticFamilyName}
                    phoneticGivenName={patient.phoneticGivenName}
                  />
                </Table.Td>
                <Table.Td>{patient.birthDate}</Table.Td>
                <Table.Td>{patient.pfPatient?.telecoms[0].formattedValue}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyContent="flex-end">
          <NotPatientButton use="white" size="m" onClick={handleSubmitByOriginal}>
            このまま作成
          </NotPatientButton>
          <Button use="base" size="m" onClick={handleSubmitByPatient} marginLeft={theme.space.m}>
            アプリ宛に作成
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
